/**
 * UpiOnboardingPrompt — iter235.
 *
 * Post-login nudge asking the user to save their UPI ID so friends can pay
 * them via GPay/PhonePe deep-links. Shows when:
 *   1. Logged-in user has no upi_id saved
 *   2. They have NOT tapped "Don't ask again" (persisted server-side)
 *   3. Not already shown this browser session
 * "Not now" closes for this session; "Don't ask again" never reminds again.
 */
import { useEffect, useState } from 'react';
import { IndianRupee, Loader2, Shield, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import api from '../lib/api';
import { claimPrompt } from '../lib/promptQueue';

const SESSION_KEY = 'upi_prompt_shown_session';

export default function UpiOnboardingPrompt() {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [upiDraft, setUpiDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id || user.upi_id || user.upi_prompt_dismissed) return;
    try { if (sessionStorage.getItem(SESSION_KEY) === 'true') return; } catch { /* ignore */ }
    const t = setTimeout(() => {
      if (!claimPrompt('upi')) return;
      try { sessionStorage.setItem(SESSION_KEY, 'true'); } catch { /* ignore */ }
      setOpen(true);
    }, 3000);
    return () => clearTimeout(t);
  }, [user?.id, user?.upi_id, user?.upi_prompt_dismissed]);

  const handleSave = async () => {
    const upi = upiDraft.trim();
    if (!upi || !upi.includes('@') || upi.includes(' ')) {
      toast.error("UPI ID should look like 'name@bank' — e.g. 9820030046@ybl");
      return;
    }
    setSaving(true);
    try {
      await api.put('/users/me', { upi_id: upi });
      await refreshUser();
      toast.success('UPI ID saved — friends can now pay you in one tap!');
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save UPI ID');
    } finally {
      setSaving(false);
    }
  };

  const handleNeverAsk = async () => {
    setOpen(false);
    try {
      await api.put('/users/me', { upi_prompt_dismissed: true });
      await refreshUser();
    } catch { /* ignore */ }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
      <DialogContent className="sm:max-w-sm rounded-2xl p-0 overflow-hidden border-2" data-testid="upi-onboarding-prompt">
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          data-testid="upi-prompt-close"
          className="absolute right-3 top-3 z-10 p-1 rounded-full hover:bg-muted transition"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
        <div className="px-6 pt-8 pb-4 text-center bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mb-3">
            <IndianRupee className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="font-display text-xl font-bold leading-tight">
            <DialogTitle asChild>
              <span>Get paid faster<br />with UPI?</span>
            </DialogTitle>
          </h2>
          <DialogDescription className="text-sm text-muted-foreground mt-2">
            Save your UPI ID once — friends settling with you get a one-tap
            <span className="font-bold text-foreground"> GPay / PhonePe </span>
            button with the amount pre-filled.
          </DialogDescription>
        </div>
        <div className="px-6 py-3 space-y-2">
          <Input
            placeholder="e.g. 9820030046@ybl"
            value={upiDraft}
            onChange={(e) => setUpiDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            className="h-11 rounded-xl text-center font-medium"
            data-testid="upi-prompt-input"
          />
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Shield className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
            <span>Only shown to people who share a group with you. Change it anytime in Profile.</span>
          </div>
        </div>
        <div className="px-6 pb-6 pt-1 space-y-2">
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || !upiDraft.trim()}
            data-testid="upi-prompt-save-btn"
            className="w-full h-12 rounded-full font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>) : 'Save my UPI ID'}
          </Button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="upi-prompt-later-btn"
              className="flex-1 h-10 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground transition"
            >
              Not now
            </button>
            <button
              type="button"
              onClick={handleNeverAsk}
              data-testid="upi-prompt-never-btn"
              className="flex-1 h-10 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground transition"
            >
              Don't ask again
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
